import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { formatCurrency, formatDate, formatPeriod } from "@/lib/format";
import StatusPill from "@/components/StatusPill";
import { BellRing, AlertCircle, TrendingUp, CalendarClock, MailCheck, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Reminders() {
  const [queue, setQueue] = useState({ overdue: [], expiring_contracts: [], upcoming_increases: [] });
  const [history, setHistory] = useState([]);

  const load = async () => {
    const [q, h] = await Promise.all([
      api.get("/reminders/queue"),
      api.get("/reminders/history"),
    ]);
    setQueue(q.data);
    setHistory(h.data);
  };

  useEffect(() => { load(); }, []);

  const send = async (payment_id) => {
    try {
      await api.post("/reminders/send", { payment_id });
      toast.success("Reminder logged");
      load();
    } catch { toast.error("Could not send"); }
  };

  return (
    <Layout title="Reminders" subtitle="Nudge overdue tenants and stay ahead of increases">
      {/* Section: Overdue */}
      <Section title="Overdue payments" icon={AlertCircle} tone="red" testid="section-overdue">
        {queue.overdue.length === 0 ? (
          <Empty text="Everyone's paid. Enjoy the quiet." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {queue.overdue.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-4 py-3.5" data-testid={`overdue-item-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.tenant?.full_name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {formatPeriod(p.period)} · Due {formatDate(p.due_date)} · {formatCurrency(p.gross)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status="overdue" />
                  <button onClick={() => send(p.id)} data-testid={`overdue-send-${p.id}`} className="inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                    <BellRing className="w-3.5 h-3.5" /> Send reminder
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Section: Increases */}
      <Section title="Upcoming rent increases" icon={TrendingUp} tone="indigo" testid="section-increases">
        {queue.upcoming_increases.length === 0 ? (
          <Empty text="No rent increases scheduled in the next 60 days." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {queue.upcoming_increases.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-4 py-3.5" data-testid={`increase-item-${c.id}`}>
                <div>
                  <div className="font-medium">{c.tenant?.full_name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Effective {formatDate(c.next_increase_date)} · +{c.increase_percent || 0}% · Current {formatCurrency(c.monthly_rent)}
                  </div>
                </div>
                <StatusPill status="upcoming_increase" />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Section: Expiring */}
      <Section title="Expiring contracts" icon={CalendarClock} tone="amber" testid="section-expiring">
        {queue.expiring_contracts.length === 0 ? (
          <Empty text="No contracts ending in the next 60 days." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {queue.expiring_contracts.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-4 py-3.5" data-testid={`expiring-item-${c.id}`}>
                <div>
                  <div className="font-medium">{c.tenant?.full_name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Ends {formatDate(c.end_date)} · {formatCurrency(c.monthly_rent)} /mo</div>
                </div>
                <StatusPill status="due_soon" label="Expiring" />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* History */}
      <Section title="Reminder history" icon={MailCheck} tone="slate" testid="section-history">
        {history.length === 0 ? (
          <Empty text="No reminders sent yet." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {history.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-4 py-3.5" data-testid={`history-item-${h.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{h.tenant_name || "—"} <span className="text-slate-400 font-normal">· {h.tenant_email || "no email"}</span></div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{h.subject} · {formatDate(h.created_at)}</div>
                </div>
                <StatusPill status={h.status === "sent" ? "paid" : (h.status === "failed" ? "overdue" : "unpaid")} label={h.status} />
              </li>
            ))}
          </ul>
        )}
      </Section>
    </Layout>
  );
}

function Section({ title, icon: Icon, tone, children, testid }) {
  const toneMap = {
    red: "bg-red-50 text-red-700",
    amber: "bg-amber-50 text-amber-700",
    indigo: "bg-indigo-50 text-indigo-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="card-flat p-6 mb-6 fade-up" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-8 h-8 rounded-lg grid place-items-center ${toneMap[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-slate-500 py-2">{text}</div>;
}
